import React from 'react';
import { 
  UserPlus, 
  Mail, 
  Key, 
  CheckCircle, 
  AlertCircle,
  Clock,
  Shield
} from 'lucide-react';

const SignupGuide = () => {
  return (
    <div className="space-y-6">
      {/* 개요 */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">AI 트레이딩 시스템 회원가입 안내</p>
            <p>안전하고 신뢰할 수 있는 자동매매를 위해 단계별 인증 과정을 거쳐주세요.</p>
          </div>
        </div>
      </div>

      {/* 단계별 가이드 */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">📝 회원가입 단계</h3>
        
        {/* 1단계 */}
        <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
            1
          </div>
          <div className="flex-1">
            <div className="flex items-center mb-2">
              <UserPlus className="h-5 w-5 text-blue-600 mr-2" />
              <h4 className="font-semibold text-gray-900">기본 정보 입력</h4>
            </div>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• 이메일 주소 (로그인 ID로 사용)</li>
              <li>• 안전한 비밀번호 (8자 이상, 영문+숫자+특수문자)</li>
              <li>• 이름 (실명 권장)</li>
            </ul>
          </div>
        </div>

        {/* 2단계 */}
        <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
            2
          </div>
          <div className="flex-1">
            <div className="flex items-center mb-2">
              <Mail className="h-5 w-5 text-green-600 mr-2" />
              <h4 className="font-semibold text-gray-900">이메일 인증</h4>
            </div>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• 가입한 이메일로 <strong>6자리 인증번호</strong> 발송</li>
              <li>• 인증번호 입력하여 이메일 소유권 확인</li>
              <li>• <span className="text-red-600">5분 내에 인증 완료 필요</span></li>
            </ul>
            <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
              <Clock className="h-4 w-4 inline mr-1" />
              인증번호가 오지 않으면 스팸함을 확인해주세요
            </div>
          </div>
        </div>

        {/* 3단계 */}
        <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
            3
          </div>
          <div className="flex-1">
            <div className="flex items-center mb-2">
              <Key className="h-5 w-5 text-purple-600 mr-2" />
              <h4 className="font-semibold text-gray-900">API 키 설정</h4>
            </div>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• 한국투자증권 KIS API 키 발급 및 입력</li>
              <li>• 계좌번호 연결</li>
              <li>• 자동매매 권한 설정</li>
            </ul>
            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
              💡 API 키 발급 방법은 설정 페이지에서 상세히 안내드립니다
            </div>
          </div>
        </div>

        {/* 4단계 */}
        <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
            4
          </div>
          <div className="flex-1">
            <div className="flex items-center mb-2">
              <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
              <h4 className="font-semibold text-gray-900">서비스 이용 시작</h4>
            </div>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• 계좌 연동 및 포트폴리오 확인</li>
              <li>• AI 분석 기반 자동매매 시작</li>
              <li>• 실시간 투자 현황 모니터링</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 보안 안내 */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start">
          <Shield className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-green-800">
            <p className="font-medium mb-1">🔒 보안 및 개인정보 보호</p>
            <ul className="space-y-1">
              <li>• API 키는 암호화되어 안전하게 저장됩니다</li>
              <li>• 개인정보는 투자 서비스 제공 목적으로만 사용됩니다</li>
              <li>• 모든 거래는 한국투자증권을 통해 안전하게 처리됩니다</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 주의사항 */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-red-800">
            <p className="font-medium mb-1">⚠️ 중요 안내사항</p>
            <ul className="space-y-1">
              <li>• 자동매매는 투자 위험을 동반합니다</li>
              <li>• 투자 전 충분한 이해와 검토가 필요합니다</li>
              <li>• 손실 가능성을 인지하고 신중하게 이용해주세요</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupGuide;